import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addErrorNotification, createFetchedData, CxmEntityType, getAllShowtimes, getFetchedDataPayload, getScreeningDatesForQuery, getSelectedSiteIds, getShowtimeIdsForQueryOnBusinessDate, getShowtimesQuery, isEmptyArray, loadAllFilmAvailabilities, loadAllFilms, loadCxmFilmsForComponent, LoadingState, NotificationType } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useI18n } from '../../context/occ/hooks/use-i18n';
import { DatePicker } from '../../subcomponents/date-picker';
import { ToggleInput } from '../../subcomponents/form/input/toggle/toggle-input';
import { ToggleInputLayout } from '../../subcomponents/form/input/toggle/types/layout';
import { ShowtimePicker } from '../../subcomponents/showtime-picker/showtime-picker';
import { ShowtimePickerMessageType as MessageType } from '../../subcomponents/showtime-picker/subcomponents/message';
import { getFilmListItemElementId } from '../../subcomponents/showtime-picker/utils/get-film-list-item-element-id';
import { getIdForComponentNotification } from '../../utils/get-id-for-component-notification';
import { scrollToElementById } from '../../utils/imperative-utils/scroll';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { dateShowtimePickerPropsValidator, defaultDateShowtimePickerPropsProvider } from './types/props';
import { getDefaultDateShowtimePickerState } from './types/state';
import { dateShowtimePickerActionReducer } from './utils/action-reducer';
import { DateShowtimePickerActionType as ActionType } from './utils/action-type';
import { isDateShowtimePickerPropsWithFilmIds, loadScreeningDates, loadShowtimes } from './utils/date-showtime-picker-utils';
import { filterScreeningDates } from './utils/filter-screening-dates';
import { getFilmIdsToDisplay } from './utils/get-film-ids-to-display';
import { getNoShowtimesForSiteDisplayTextProps } from './utils/get-no-showtimes-for-site-display-text-props';
import { getSelectedBusinessDate } from './utils/get-selected-business-date';
import { handleDateSelected } from './utils/handle-date-selected';
//import { setupFilter } from './utils/setup-filter';
import { browsingDomainStore } from "@vista/omnichannel-components-domain";
import { getShowtimeById } from "@vista/omnichannel-components-domain";
import { createShowtimeFilter } from '@vista/omnichannel-components-domain';
import { validateShowtimePickerData } from './utils/validate';
const b = bemClassBuilder('v-date-showtime-picker');
export const DateShowtimePicker = observer(props => {
	console.log("render")
    var _a;
    const i18n = useI18n();
    const { filmIds, config, displayText } = props;
    const { filter, componentId, datePicker } = config;
    const siteIds = (_a = props.siteIds) !== null && _a !== void 0 ? _a : getSelectedSiteIds();
    const filterId = filter === null || filter === void 0 ? void 0 : filter.filterId;
    const defaultDateShowtimePickerState = getDefaultDateShowtimePickerState(props);
    const [state, dispatch] = useReducer(dateShowtimePickerActionReducer, defaultDateShowtimePickerState);
    const { oldShowtimeIdsForDate, selectedBusinessDate, apiErrorMessageType, filmsLoadingState, isLoadingAllFilms, isExternalDateTransition, showAllFilms } = state;
    const showtimesQuery = getShowtimesQuery(filmIds, siteIds);
    const screeningDates = getScreeningDatesForQuery(showtimesQuery);
    const filteredScreeningDates = screeningDates && filterId ? filterScreeningDates(screeningDates, filterId) : screeningDates;
    const datePickerActiveDates = filteredScreeningDates === null || filteredScreeningDates === void 0 ? void 0 : filteredScreeningDates.map(screeningDate => screeningDate.businessDate);
    const showtimeIdsForDate = useMemo(() => {
        return selectedBusinessDate
            ? getShowtimeIdsForQueryOnBusinessDate(getShowtimesQuery(filmIds, siteIds), selectedBusinessDate)
            : createFetchedData(LoadingState.NotLoaded);
    }, [filmIds, selectedBusinessDate, siteIds]);
    const handleDateClicked = useCallback(async (businessDate) => {
        const query = getShowtimesQuery(filmIds, siteIds);
        dispatch({ type: ActionType.SelectDate, date: businessDate });
        await handleDateSelected(query, businessDate, dispatch);
    }, [filmIds, siteIds]);
    const handleAllFilmsToggleClicked = useCallback(async (show) => {
        dispatch({ type: ActionType.ToggleAllFilms, showAllFilms: show });
        if (!show)
            return;
        dispatch({ type: ActionType.LoadingAllFilms });
        try {
            await loadAllFilms();
            dispatch({ type: ActionType.LoadedAllFilms });
        }
        catch (error) {
            addErrorNotification({
                id: getIdForComponentNotification('date-showtime-picker', NotificationType.Error, 'show-all-films'),
                messages: ['Failed to load films, please try again later']
            }, error);
            dispatch({ type: ActionType.FailedToLoadAllFilms });
        }
    }, []);
    useEffect(() => {
        (async () => {
            const query = getShowtimesQuery(filmIds, siteIds);
            dispatch({ type: ActionType.Loading });
            if (!query.siteIds || isEmptyArray(query.siteIds))
                return;
            try {
                await Promise.all([
                    loadScreeningDates(query),
                    //loadShowtimes(query, selectedBusinessDate), // HACK - need all showtimes to be loaded
                    loadCxmFilmsForComponent(CxmEntityType.FilmShowtimePicker, componentId),
                    handleAllFilmsToggleClicked(showAllFilms),
                    loadAllFilmAvailabilities()
                ]);
                // We don't want to execute this effect each time the selected business date changes, but we do want to use the initial value prior to a re-render,
                // hence using the return value here instead of including selectedBusinessDate as a dependency of this useEffect.
                const dates = getScreeningDatesForQuery(query);
                const filteredDates = dates && filterId ? filterScreeningDates(dates, filterId) : dates;
				// HACK - loading showtimes from ALL the filtered dates so we can get all the attributes loaded
				await Promise.all(filteredDates.map(async date => await loadShowtimes(query, date.businessDate)));
                const selectedDate = await attemptSetSelectedDate(filteredDates);
                setupFilter(query, filter, selectedDate);
                dispatch({ type: ActionType.SetIsExternalDateTransition, isExternalDateTransition: true });
                dispatch({ type: ActionType.Loaded });
            }
            catch (error) {
                dispatch({ type: ActionType.FailedToLoad, error: error });
            }
        })();
        // Stringifying the siteIds, because when an array is passed in, useEffect does not re-run.
        // This is because React is not able to identify the difference between the mutated SiteIds arrays by comparing only their references.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(siteIds), filter]);
    useEffect(() => {
        const currentShowtimeIds = getFetchedDataPayload(showtimeIdsForDate);
        if (!filterId || !currentShowtimeIds)
            return;
        // If the current showtimes have changed, update filter so that it has the most recent data.
        if (currentShowtimeIds.length !== oldShowtimeIdsForDate.length ||
            !currentShowtimeIds.every((id, index) => id === oldShowtimeIdsForDate[index])) {
            const query = getShowtimesQuery(filmIds, siteIds);
            setupFilter(query, filter, selectedBusinessDate);
            dispatch({ type: ActionType.SetOldShowtimeIdsForDate, showtimeIds: currentShowtimeIds });
        }
    }, [oldShowtimeIdsForDate, filterId, showtimeIdsForDate, filter, filmIds, siteIds, selectedBusinessDate]);
    const handleDateChangedExternally = useCallback(async (date, filmId) => {
        await handleDateClicked(date);
        dispatch({ type: ActionType.SetIsExternalDateTransition, isExternalDateTransition: true });
        if (filmId)
            scrollToElementById(getFilmListItemElementId(filmId));
    }, [handleDateClicked]);
    /**
     * Once isExternalDateTransition becomes true, a date has externally been changed and the DatePicker component
     * will scroll the slide into view. This effect will reset the transition afterwards.
     */
    useEffect(() => {
        dispatch({ type: ActionType.SetIsExternalDateTransition, isExternalDateTransition: false });
    }, [isExternalDateTransition]);
    /**
     * If selectedBusinessDate is already set, or the data in the browsing store is not available, this method does nothing.
     * Returns the updated selected business date immediately.
     */
    const attemptSetSelectedDate = useCallback(async (dates) => {
        const selectedDate = getSelectedBusinessDate(datePicker.selectedBusinessDate, dates);
        dispatch({ type: ActionType.SelectDate, date: selectedDate });
        await handleDateChangedExternally(selectedDate);
        return selectedDate;
    }, [datePicker.selectedBusinessDate, handleDateChangedExternally]);
    const showtimes = getAllShowtimes(selectedBusinessDate, filmIds, siteIds, filterId);
    const filmIdsToDisplay = getFilmIdsToDisplay({
        showtimes,
        showAllFilms,
        isLoading: filmsLoadingState !== LoadingState.Success,
        filmIds,
        filterId
    });
	
	

    // Check for invalid states - e.g. empty data, or no sites selected.
    const invalidStateMessage = apiErrorMessageType
        ? { messageType: apiErrorMessageType }
        : validateShowtimePickerData(screeningDates, showtimesQuery, filmIdsToDisplay, filmsLoadingState, config);
    const hideAccessories = invalidStateMessage &&
        (invalidStateMessage.messageType === MessageType.FailedToLoadData ||
            invalidStateMessage.messageType === MessageType.NoCinemasSelected ||
            invalidStateMessage.messageType === MessageType.NoShowtimesAllDates);
    const hideDatePicker = !datePicker.show || hideAccessories;
    const isAllFilmsToggleVisible = () => {
        if (isDateShowtimePickerPropsWithFilmIds(props))
            return true;
        return !props.config.allFilmsToggle.show || hideAccessories;
    };
    const handleGetNoShowtimesForSiteDisplayTextProps = (siteId, filmId) => getNoShowtimesForSiteDisplayTextProps(displayText, siteId, filmId, filteredScreeningDates, selectedBusinessDate, handleDateChangedExternally, i18n);
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [!hideDatePicker && (_jsx(DatePicker, { isLoading: !screeningDates, businessDates: datePickerActiveDates !== null && datePickerActiveDates !== void 0 ? datePickerActiveDates : [], selectedBusinessDate: selectedBusinessDate, dateExternallyClicked: isExternalDateTransition, onDateClicked: handleDateClicked, dateFormat: displayText.datePickerDateFormat, todayDateFormat: displayText.datePickerTodayDateFormat, carouselConfig: datePicker.carousel })), !isAllFilmsToggleVisible() && (_jsx(ToggleInput, { label: displayText.allFilmsToggleLabel, value: showAllFilms, className: b.element('all-films-toggle'), layout: ToggleInputLayout.InputFirst, disabled: filmsLoadingState !== LoadingState.Success || isLoadingAllFilms, loading: isLoadingAllFilms, onChangeValue: handleAllFilmsToggleClicked })), _jsx(ShowtimePicker, Object.assign({}, props, { config: config, showtimes: showtimes, filmsLoadingState: filmsLoadingState, filmIds: filmIdsToDisplay, siteIds: siteIds, getNoShowtimesForSiteDisplayTextProps: handleGetNoShowtimesForSiteDisplayTextProps, groupShowtimesByDate: false, invalidStateMessage: invalidStateMessage }))] })));
});
DateShowtimePicker.displayName = 'DateShowtimePicker';
/**
 * The **Date Showtime Picker** component renders a list of showtimes for a given set of films and sites for specified dates.
 */
export const DateShowtimePickerComponent = withComponentWrapper(DateShowtimePicker, defaultDateShowtimePickerPropsProvider, dateShowtimePickerPropsValidator);

// Hack - https://vistadigital.atlassian.net/browse/CRZN-1363 - Alternative setupFilter function gets showtimes from ALL dates
	// This prevents some instances where not all the filters appear on some dates. Should be fixed by OCC upgrade at some point
export const setupFilter = (query, filterConfig, businessDate) => {
    if (!filterConfig || !businessDate)
        return;
    const { filterId, options } = filterConfig;
	console.log("browsingDomainStore", browsingDomainStore)

    const showtimesObject = browsingDomainStore.data.showtimesById
    let showtimes = []

    for (const showtimeId in showtimesObject) {
		
        if (!query?.filmIds || query?.filmIds?.includes(showtimesObject[showtimeId]?.payload?.filmId) && query?.siteIds?.includes(showtimesObject[showtimeId]?.payload?.siteId)) {
            showtimes.push(getShowtimeById(showtimeId))
            console.log("pushing this showtime: ", getShowtimeById(showtimeId))
        }
      }
    
    createShowtimeFilter(filterId, showtimes, options);
};